import { useState, type FormEvent } from 'react';
import type { RateRule } from '@arcnet/contracts';
import { useSaveRate, useRemoveRate } from '../../lib/api/hooks-billing.js';
import { ErrorBanner, Badge, SubHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatMoney } from '../../lib/format.js';

/**
 * Editing what a call costs (owner, 25 September 2026: "can we make the rates editable?").
 *
 * A published sheet is never changed. Calls already invoiced were priced by it, and re-pricing
 * them afterwards is the one thing a billing system may not do (ADR-015) -- so an edit forks a
 * DRAFT from the live sheet, carries every rule into it, and changes the one rate there. Nothing
 * a customer is charged moves until somebody publishes.
 *
 * That is stated on the screen rather than implied, because the alternative is somebody editing a
 * rate, watching a call be billed at the old one, and concluding the platform is broken.
 */

const BANDS = ['anytime', 'peak', 'offpeak', 'weekend'] as const;

/** What the API takes, from what the form holds. Money stays a string all the way down. */
function ruleBody(d: Draft): Record<string, unknown> {
  return {
    prefix: d.prefix.replace(/[^0-9]/g, ''),
    description: d.description.trim() || undefined,
    time_band: d.time_band,
    rate_per_minute: money(d.rate_per_minute),
    connection_fee: money(d.connection_fee),
    minimum_charge: money(d.minimum_charge),
    free_seconds: Number(d.free_seconds) || 0,
    first_increment_sec: Number(d.first_increment_sec) || 1,
    subsequent_increment_sec: Number(d.subsequent_increment_sec) || 1,
  };
}

/** Money is a string end to end (MoneySchema): a float would round somebody's invoice. */
const money = (v: string): string => {
  const t = v.trim().replace(',', '.');
  return /^\d+(\.\d{1,6})?$/.test(t) ? t : '0';
};

export interface Draft {
  prefix: string; description: string; time_band: string;
  rate_per_minute: string; connection_fee: string; minimum_charge: string;
  free_seconds: string; first_increment_sec: string; subsequent_increment_sec: string;
}

export const emptyDraft = (): Draft => ({
  prefix: '', description: '', time_band: 'anytime',
  rate_per_minute: '', connection_fee: '0', minimum_charge: '0',
  free_seconds: '0', first_increment_sec: '1', subsequent_increment_sec: '1',
});

export const draftOf = (r: RateRule): Draft => ({
  prefix: r.prefix, description: r.description ?? '', time_band: r.time_band,
  rate_per_minute: r.rate_per_minute, connection_fee: r.connection_fee,
  minimum_charge: r.minimum_charge, free_seconds: String(r.free_seconds),
  first_increment_sec: String(r.first_increment_sec),
  subsequent_increment_sec: String(r.subsequent_increment_sec),
});

/**
 * One rate, being edited in place.
 *
 * In the row it belongs to rather than in a dialog: a rate is read against the ones above and
 * below it -- 27 against 2782, mobile against landline -- and a dialog hides exactly that.
 */
export function RateRow({ deckId, rule, currency, onDone }: {
  deckId: string; rule: RateRule; currency: string; onDone: () => void;
}) {
  const save = useSaveRate(deckId);
  const remove = useRemoveRate(deckId);
  const step = useStepUp();
  const [d, setD] = useState<Draft>(draftOf(rule));

  const set = (k: keyof Draft) => (e: { target: { value: string } }) =>
    setD((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    void step.run(() => save.mutateAsync(ruleBody(d))).then((ok) => { if (ok) onDone(); });
  };

  return (
    <tr>
      <td colSpan={7} style={{ padding: 0 }}>
        <form className="arc-card-body" onSubmit={submit}
          style={{ background: 'var(--arc-surface-2)', borderLeft: '2px solid var(--arc-accent)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`r-pfx-${rule.id}`}>Prefix</label>
              {/* The prefix and band identify the rule; changing them here would write a second
                  rate rather than change this one, so they are shown and not edited. */}
              <input id={`r-pfx-${rule.id}`} className="arc-input arc-mono" value={`+${d.prefix}`} readOnly />
            </div>
            <div className="arc-field" style={{ margin: 0, gridColumn: 'span 2' }}>
              <label className="arc-label" htmlFor={`r-desc-${rule.id}`}>Destination</label>
              <input id={`r-desc-${rule.id}`} className="arc-input" value={d.description}
                maxLength={200} onChange={set('description')} placeholder="South Africa — mobile" />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`r-rate-${rule.id}`}>Per minute ({currency})</label>
              <input id={`r-rate-${rule.id}`} className="arc-input arc-num" inputMode="decimal"
                value={d.rate_per_minute} onChange={set('rate_per_minute')} required />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`r-conn-${rule.id}`}>Connection fee</label>
              <input id={`r-conn-${rule.id}`} className="arc-input arc-num" inputMode="decimal"
                value={d.connection_fee} onChange={set('connection_fee')} />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`r-min-${rule.id}`}>Minimum charge</label>
              <input id={`r-min-${rule.id}`} className="arc-input arc-num" inputMode="decimal"
                value={d.minimum_charge} onChange={set('minimum_charge')} />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`r-inc1-${rule.id}`}>First increment (s)</label>
              <input id={`r-inc1-${rule.id}`} className="arc-input arc-num" inputMode="numeric"
                value={d.first_increment_sec} onChange={set('first_increment_sec')} />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`r-inc2-${rule.id}`}>Then every (s)</label>
              <input id={`r-inc2-${rule.id}`} className="arc-input arc-num" inputMode="numeric"
                value={d.subsequent_increment_sec} onChange={set('subsequent_increment_sec')} />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`r-free-${rule.id}`}>Free seconds</label>
              <input id={`r-free-${rule.id}`} className="arc-input arc-num" inputMode="numeric"
                value={d.free_seconds} onChange={set('free_seconds')} />
            </div>
          </div>

          <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
            <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm"
              disabled={save.isPending || step.busy}>
              {save.isPending || step.busy ? 'Saving…' : 'Save to draft'}
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={onDone}>Cancel</button>
            <div className="arc-spacer" />
            <RowAction kind="delete" label={`Remove +${rule.prefix}`}
              disabled={remove.isPending}
              onClick={async () => {
                if (!(await confirmAction({
                  title: `Remove +${rule.prefix} from this sheet?`,
                  body: 'Calls to it will be refused, not free.',
                  action: 'Remove', danger: true,
                }))) return;
                void step.run(() => remove.mutateAsync({ prefix: rule.prefix, time_band: rule.time_band }))
                  .then((ok) => { if (ok) onDone(); });
              }} />
          </div>
          {save.error && <ErrorBanner error={save.error} />}
          {remove.error && <ErrorBanner error={remove.error} />}
          <StepUpPrompt step={step} what="Changing a rate">
            A rate decides what every call to this destination costs, so it is confirmed like
            anything else that moves money.
          </StepUpPrompt>
        </form>
      </td>
    </tr>
  );
}

/** A destination the sheet does not price yet. */
export function AddRate({ deckId, currency, onDone }: {
  deckId: string; currency: string; onDone: () => void;
}) {
  const save = useSaveRate(deckId);
  const step = useStepUp();
  const [d, setD] = useState<Draft>(emptyDraft());
  const set = (k: keyof Draft) => (e: { target: { value: string } }) =>
    setD((p) => ({ ...p, [k]: e.target.value }));

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void step.run(() => save.mutateAsync(ruleBody(d))).then((ok) => { if (ok) onDone(); });
      }}>
      <SubHead>Add a rate</SubHead>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="nr-pfx">Prefix</label>
          <input id="nr-pfx" className="arc-input arc-mono" value={d.prefix} required
            placeholder="27821" onChange={(e) => setD((p) => ({ ...p, prefix: e.target.value.replace(/[^0-9]/g, '') }))} />
          <div className="arc-hint">Digits, no +. The longest match wins.</div>
        </div>
        <div className="arc-field" style={{ margin: 0, gridColumn: 'span 2' }}>
          <label className="arc-label" htmlFor="nr-desc">Destination</label>
          <input id="nr-desc" className="arc-input" value={d.description} maxLength={200}
            onChange={set('description')} placeholder="South Africa — Vodacom" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="nr-band">Band</label>
          <select id="nr-band" className="arc-select" value={d.time_band} onChange={set('time_band')}>
            {BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="nr-rate">Per minute ({currency})</label>
          <input id="nr-rate" className="arc-input arc-num" inputMode="decimal" required
            value={d.rate_per_minute} onChange={set('rate_per_minute')} placeholder="0.89" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="nr-conn">Connection fee</label>
          <input id="nr-conn" className="arc-input arc-num" inputMode="decimal"
            value={d.connection_fee} onChange={set('connection_fee')} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="nr-inc1">First increment (s)</label>
          <input id="nr-inc1" className="arc-input arc-num" inputMode="numeric"
            value={d.first_increment_sec} onChange={set('first_increment_sec')} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="nr-inc2">Then every (s)</label>
          <input id="nr-inc2" className="arc-input arc-num" inputMode="numeric"
            value={d.subsequent_increment_sec} onChange={set('subsequent_increment_sec')} />
        </div>
      </div>
      <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
        <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm"
          disabled={save.isPending || step.busy || !d.prefix || !d.rate_per_minute}>
          {save.isPending || step.busy ? 'Saving…' : 'Add to draft'}
        </button>
        <button type="button" className="arc-btn arc-btn-sm" style={{ marginLeft: 8 }} onClick={onDone}>Cancel</button>
        <button type="button" className="arc-btn arc-btn-sm" onClick={onDone}>Cancel</button>
      </div>
      {save.error && <ErrorBanner error={save.error} />}
      <StepUpPrompt step={step} what="Adding a rate">
        A rate decides what every call to this destination costs.
      </StepUpPrompt>
    </form>
  );
}

/**
 * What is live, what is waiting, and the button that moves one to the other.
 *
 * Shown whenever a sheet has an unpublished version, because a draft nobody publishes is a set of
 * rates somebody believes are in force and are not.
 */
export function DraftBanner({ draft, published, rules, onView, viewing, onReview }: {
  draft: { version_number: number; rule_count?: number } | null;
  published: { version_number: number; effective_from: string | null } | null;
  rules: number;
  onView: (version: number | null) => void;
  viewing: number | null;
  /** Opens the review (billing/RateSheetTools PublishReview), the only way to publish. */
  onReview: () => void;
}) {
  if (!draft) {
    return published ? (
      <div className="arc-row" style={{ gap: 8, marginBottom: 10 }}>
        <Badge tone="ok">v{published.version_number} live</Badge>
        <span className="arc-subtle" style={{ fontSize: 12 }}>
          {published.effective_from
            ? `since ${new Date(published.effective_from).toLocaleDateString('en-ZA')}`
            : ''}
          {' · '}{rules} rates. Editing one starts a draft; nothing changes until it is published.
        </span>
      </div>
    ) : null;
  }

  return (
    <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }}>
      <Icon name="warning" size={14} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>
          Draft v{draft.version_number} is not live{draft.rule_count != null ? ` · ${draft.rule_count} rates` : ''}
        </div>
        <div style={{ fontSize: 12.5, marginTop: 2 }}>
          Calls are still priced by {published ? `v${published.version_number}` : 'nothing'} until
          this is published. Publishing closes the live version at that moment, so no two sheets
          ever overlap.
        </div>
        <div className="arc-row" style={{ gap: 8, marginTop: 8 }}>
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={onReview}>
            Review and publish v{draft.version_number}
          </button>
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => onView(viewing === draft.version_number ? null : draft.version_number)}>
            {viewing === draft.version_number ? 'Show what is live' : 'Show the draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The read-only money cell, so the table and the editor agree on how a rate looks. */
export const rateCell = (v: string, currency: string): string => formatMoney(v, currency);
